import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Edit, Archive, Plus, FolderOpen } from 'lucide-react';
import { useAssociateAccount } from '../hooks/useAssociateAccounts.js';
import { useDeleteAccount } from '../hooks/useAssociateAccounts.js';
import AssociateAccountForm from '../components/AssociateAccountForm.jsx';
import AssociateTrancheForm from '../components/AssociateTrancheForm.jsx';
import LoadingSpinner from '../components/LoadingSpinner.jsx';
import Button from '../components/Button.jsx';
import { formatCurrency, formatDate } from '../lib/format.js';

// ── Field ─────────────────────────────────────────────────────────────────────

function Field({ label, value }) {
  if (value == null || value === '') return null;
  return (
    <div className="flex justify-between items-start gap-3 py-1.5 text-sm border-b border-slate-50 last:border-0">
      <span className="text-slate-500 shrink-0">{label}</span>
      <span className="text-slate-800 text-right break-words">{value}</span>
    </div>
  );
}

// ── Tranche row ───────────────────────────────────────────────────────────────

function TrancheRow({ tranche, onClick }) {
  const balance = tranche.balance_cents ?? 0;
  const isOpen  = tranche.status === 'open';

  return (
    <div
      onClick={() => onClick(tranche)}
      className="flex items-center gap-3 py-3 px-4 cursor-pointer hover:bg-slate-50 transition-colors border-b border-slate-50 last:border-0"
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === 'Enter' && onClick(tranche)}
      aria-label={`Voir la tranche ${tranche.label}`}
    >
      <div className="flex-1 min-w-0">
        <p className="font-medium text-slate-900 text-sm truncate">{tranche.label}</p>
        <p className="text-xs text-slate-500">Ouverte le {formatDate(tranche.opened_at)}</p>
      </div>
      <div className="text-right shrink-0">
        <p className={`text-sm font-semibold tabular-nums ${balance < 0 ? 'text-red-600' : 'text-emerald-700'}`}>
          {formatCurrency(balance)}
        </p>
      </div>
      <span
        className={`shrink-0 text-xs px-2 py-0.5 rounded-full font-medium ${
          isOpen ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'
        }`}
      >
        {isOpen ? 'Ouverte' : 'Clôturée'}
      </span>
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function AssociateAccountDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const { data, isLoading, error } = useAssociateAccount(id);
  const deleteAccount = useDeleteAccount();

  const [editOpen,   setEditOpen]   = useState(false);
  const [trancheFormOpen, setTrancheFormOpen] = useState(false);

  if (isLoading) {
    return (
      <div className="flex justify-center py-16">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
        Impossible de charger le compte : {error.message}
      </div>
    );
  }

  if (!data) return null;

  const account  = data;
  const tranches = data.tranches ?? [];
  const associate = data.associate ?? null;
  const balance  = account.balance_cents ?? 0;

  async function handleArchive() {
    if (!confirm('Archiver ce compte associé ? Cette action ne peut pas être annulée si des tranches sont liées.')) return;
    try {
      await deleteAccount.mutateAsync(account.id);
      navigate('/associes');
    } catch (err) {
      if (err.message?.includes('RESTRICT') || err.status === 400) {
        alert('Supprimez d\'abord les tranches et les mouvements liés à ce compte.');
      }
    }
  }

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-start gap-3">
        <button
          type="button"
          onClick={() => navigate('/associes')}
          className="mt-0.5 p-1.5 rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-700 transition-colors shrink-0"
          aria-label="Retour à la liste des associés"
        >
          <ArrowLeft size={18} />
        </button>

        <div className="flex-1 min-w-0">
          <h1 className="text-lg font-semibold text-slate-900 truncate">
            Compte courant — {associate?.display_name ?? associate?.first_name ?? '—'}
          </h1>
          <div className="flex flex-wrap items-center gap-2 mt-1">
            <span
              className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                account.status === 'active'
                  ? 'bg-emerald-100 text-emerald-700'
                  : 'bg-slate-100 text-slate-500'
              }`}
            >
              {account.status === 'active' ? 'Actif' : 'Clôturé'}
            </span>
          </div>
        </div>

        <div className="flex gap-2 shrink-0">
          <Button variant="ghost" className="text-xs gap-1" onClick={() => setEditOpen(true)}>
            <Edit size={13} /> Modifier
          </Button>
          <Button
            variant="danger"
            className="text-xs gap-1"
            onClick={handleArchive}
            loading={deleteAccount.isPending}
          >
            <Archive size={13} /> Archiver
          </Button>
        </div>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Informations associé */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm px-4 py-4">
          <h2 className="text-xs font-semibold uppercase text-slate-500 mb-3 tracking-wide">Associé</h2>
          {associate && (
            <>
              <Field
                label={associate.kind === 'company' ? 'Raison sociale' : 'Nom complet'}
                value={associate.display_name ?? `${associate.first_name ?? ''} ${associate.last_name ?? ''}`.trim()}
              />
              {associate.kind === 'company' && associate.siren && (
                <Field label="SIREN" value={associate.siren} />
              )}
              <Field label="Email" value={associate.email} />
              {associate.notes && (
                <div className="py-1.5 text-sm">
                  <span className="text-slate-500">Notes</span>
                  <p className="mt-1 text-slate-700 italic bg-slate-50 rounded px-3 py-2 text-xs">
                    {associate.notes}
                  </p>
                </div>
              )}
            </>
          )}
          <Field label="Ouvert le"      value={formatDate(account.opened_at)} />
          {account.ownership_pct != null && (
            <Field label="Part de détention" value={`${account.ownership_pct} %`} />
          )}
          {account.notes && (
            <div className="py-1.5 text-sm">
              <span className="text-slate-500">Notes du compte</span>
              <p className="mt-1 text-slate-700 italic bg-slate-50 rounded px-3 py-2 text-xs">
                {account.notes}
              </p>
            </div>
          )}
        </div>

        {/* Solde global */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm px-4 py-4">
          <h2 className="text-xs font-semibold uppercase text-slate-500 mb-3 tracking-wide">Solde global du compte</h2>
          <p
            className={`text-3xl font-bold tabular-nums ${
              balance < 0 ? 'text-red-700' : 'text-emerald-700'
            }`}
          >
            {formatCurrency(balance)}
          </p>
          <p className="text-xs text-slate-500 mt-1">
            Somme des soldes de {tranches.length} tranche{tranches.length > 1 ? 's' : ''}
          </p>
          <div className="mt-4 grid grid-cols-2 gap-3">
            <div>
              <p className="text-xs text-slate-500">Tranches ouvertes</p>
              <p className="text-base font-semibold text-slate-900">
                {tranches.filter((t) => t.status === 'open').length}
              </p>
            </div>
            <div>
              <p className="text-xs text-slate-500">Tranches clôturées</p>
              <p className="text-base font-semibold text-slate-900">
                {tranches.filter((t) => t.status === 'closed').length}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Tranches */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
          <h2 className="text-sm font-semibold text-slate-800">
            Tranches
            <span className="ml-2 text-xs font-normal text-slate-500">({tranches.length})</span>
          </h2>
          <Button
            className="text-xs gap-1 py-1.5 px-3"
            onClick={() => setTrancheFormOpen(true)}
          >
            <Plus size={13} /> Créer une tranche
          </Button>
        </div>

        {tranches.length === 0 ? (
          <div className="px-6 py-12 text-center">
            <FolderOpen size={36} className="mx-auto text-slate-300 mb-3" />
            <p className="text-sm text-slate-500">Aucune tranche créée</p>
            <p className="text-xs text-slate-400 mt-1">
              Créez une première tranche pour y enregistrer des apports ou retraits.
            </p>
          </div>
        ) : (
          <div className="divide-y divide-slate-50">
            {tranches.map((t) => (
              <TrancheRow
                key={t.id}
                tranche={t}
                onClick={(tranche) => navigate(`/associes/comptes/${id}/tranches/${tranche.id}`)}
              />
            ))}
          </div>
        )}
      </div>

      <AssociateAccountForm
        open={editOpen}
        onClose={() => setEditOpen(false)}
        account={account}
      />
      <AssociateTrancheForm
        open={trancheFormOpen}
        onClose={() => setTrancheFormOpen(false)}
        accountId={id}
      />
    </div>
  );
}
